import React from "react";
import { useGame } from "../game/em2c-store";
import {
  Skull,
  Sun,
  Users,
  Building2,
  FlaskConical,
  RotateCcw,
} from "lucide-react";

export default function GameOver() {
  const defeat = useGame((s) => s.defeat);
  const defeatReason = useGame((s) => s.defeatReason);

  const sol = useGame((s) => s.sol ?? 1);
  const colonistsCount = useGame((s) => (s.colonists ? s.colonists.length : 0));
  const buildingsCount = useGame((s) =>
    s.buildings ? Math.max(0, s.buildings.length - 1) : 0
  );
  const researchCount = useGame((s) =>
    s.research && s.research.completed ? s.research.completed.length : 0
  );

  // Mars Colony currently has no success/victory ending. The game runs
  // indefinitely unless a true colony-loss condition is reached.
  if (!defeat) return null;

  const accentColor = "#ff3366";
  const titleText = "COLONY LOST";
  const subtitleText = defeatReason || "Life support failure.";

  const handleNewGame = () => {
    try {
      const resetFn = useGame.getState().reset;
      if (typeof resetFn === "function") {
        resetFn();
      } else {
        window.location.reload();
      }
    } catch (err) {
      window.location.reload();
    }
  };

  return (
    <div
      data-testid="game-over-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 select-none pointer-events-auto"
      style={{ fontFamily: "'Rajdhani', sans-serif" }}
    >
      <div
        className="relative w-full max-w-md bg-[#0a0c10] border rounded-lg p-6 md:p-8 shadow-2xl flex flex-col items-center text-center overflow-hidden"
        style={{
          borderColor: accentColor,
          boxShadow: `0 0 30px ${accentColor}25, inset 0 0 15px ${accentColor}10`,
        }}
      >
        <div
          className="absolute top-0 left-0 right-0 h-1"
          style={{ backgroundColor: accentColor }}
        />

        <div
          className="mb-4 p-4 rounded-full border flex items-center justify-center"
          style={{
            borderColor: `${accentColor}50`,
            backgroundColor: `${accentColor}15`,
            color: accentColor,
            boxShadow: `0 0 20px ${accentColor}30`,
          }}
        >
          <Skull className="w-12 h-12 stroke-[1.75]" />
        </div>

        <h1
          className="text-3xl md:text-4xl font-bold uppercase tracking-widest mb-2 animate-pulse"
          style={{
            color: accentColor,
            textShadow: `0 0 12px ${accentColor}80`,
          }}
        >
          {titleText}
        </h1>

        <p className="text-slate-300 font-mono text-sm leading-relaxed mb-6">
          {subtitleText}
        </p>

        <div className="w-full h-px bg-white/10 mb-6" />

        <div className="w-full grid grid-cols-2 gap-3 mb-8">
          <div className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-md">
            <div className="p-2 rounded bg-amber-500/10 text-amber-400 shrink-0">
              <Sun className="w-5 h-5" />
            </div>
            <div className="text-left min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 truncate">
                Sol Reached
              </div>
              <div className="text-lg font-bold text-white font-mono">{sol}</div>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-md">
            <div className="p-2 rounded bg-cyan-500/10 text-cyan-400 shrink-0">
              <Users className="w-5 h-5" />
            </div>
            <div className="text-left min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 truncate">
                Colonists Survived
              </div>
              <div className="text-lg font-bold text-white font-mono">{colonistsCount}</div>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-md">
            <div className="p-2 rounded bg-emerald-500/10 text-emerald-400 shrink-0">
              <Building2 className="w-5 h-5" />
            </div>
            <div className="text-left min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 truncate">
                Buildings Built
              </div>
              <div className="text-lg font-bold text-white font-mono">{buildingsCount}</div>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 bg-white/5 border border-white/10 rounded-md">
            <div className="p-2 rounded bg-purple-500/10 text-purple-400 shrink-0">
              <FlaskConical className="w-5 h-5" />
            </div>
            <div className="text-left min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 truncate">
                Research Unlocked
              </div>
              <div className="text-lg font-bold text-white font-mono">{researchCount}</div>
            </div>
          </div>
        </div>

        <button
          data-testid="btn-new-game"
          onClick={handleNewGame}
          className="w-full py-3 px-6 rounded border font-mono uppercase tracking-widest text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200 hover:brightness-125 active:scale-[0.98] cursor-pointer"
          style={{
            borderColor: accentColor,
            backgroundColor: `${accentColor}20`,
            color: accentColor,
            boxShadow: `0 0 15px ${accentColor}20`,
          }}
        >
          <RotateCcw className="w-4 h-4" />
          <span>New Game</span>
        </button>
      </div>
    </div>
  );
}
